import type {
  DatabaseBlockModel,
  DatabaseBlockProps,
} from '@blocksuite/affine-model';
import {
  createDatabaseReferenceProps,
  createDatabaseShardManifest,
  createDatabaseShardManifestEntry,
  createShardedDatabaseIdentityProps,
  DATABASE_ROW_SHARD_ORDER_ARRAY_KEY,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
} from '@blocksuite/affine-model';
import { dataViewI18nT } from '@blocksuite/data-view';
import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';
import { isSerializedXYWH } from '@blocksuite/global/gfx';
import {
  type BlockModel,
  createDocBindingExtension,
  type Doc as StoreDoc,
  Store,
  Text,
} from '@blocksuite/store';
import {
  blockHasDatabaseShardDirectory,
  classifyPageSubdocId,
  createServerPagePartDocId,
  databaseDocumentCollectionRoot,
  DatabaseShardDirectoryFormatError,
  databaseShardDirectoryPartDocId,
  databaseShardDirectoryPartDocIds,
  databaseShardDirectoryPartId,
  type DatabaseShardDirectoryPartMaps,
  type DatabaseShardDirectoryStoredHead,
  deleteDatabaseShardDirectory,
  FILE_INDEX_MANAGED_BY,
  PAGE_DOCUMENT_PART_ID,
  pageBlockIdsOfKind,
  pageReadSource,
  readDatabaseShardDirectoryBlockHead,
  resetDatabaseShardDirectoryPart,
  summarizeDatabaseShardDirectoryPart,
  UnsupportedDatabaseStorageVersionError,
  writeDatabaseShardDirectoryHead,
} from '@streamlain/database-identity';
import {
  localPublicationApplyOrigin,
  type LocalPublicationReceipt,
  type PublicationCompanion,
  PublicationError,
  type PublicationMemberBase,
} from '@streamlain/doc-publication-contract';
import {
  AbstractType,
  applyUpdate,
  Doc as YDoc,
  encodeStateAsUpdate,
  Map as YMap,
} from 'yjs';

import { createCanvasSummaryPresentationState } from './canvas-presentation-state.js';
import type { DatabaseBlockDataSource } from './data-source.js';
import {
  publicationDirectoryPartMaps,
  publicationStageDocs,
  readPublicationDirectory,
  writeDatabaseRootDirectory,
} from './data-source-directory-write.js';
import {
  bindDatabaseCreationStage,
  DatabasePublicationStage,
  getDatabaseWriterPorts,
} from './data-source-publication.js';
import {
  ensureSystemWorkspaceDoc,
  getSystemWorkspaceStore,
} from './data-source-storage.js';
import {
  getFileIndexShardRegistry,
  nextFileIndexWriterShardId,
  writeFileIndexShardEntry,
} from './file-index-shard-registry.js';
import { textFromString } from './properties/text-from-string.js';

/**
 * Title for a database created from the UI without an explicit name, in the
 * current UI language. Existing titles are stored data and never rewritten.
 */
export function defaultDatabaseTitle(): string {
  return dataViewI18nT('database.untitled');
}

export type ShardedDatabaseRootCreationInput = {
  databaseId?: string;
  title?: string;
  managedBy?: string;
};

export type ShardedDatabaseReferenceProps = ReturnType<
  typeof createDatabaseReferenceProps
> & { id: string };

export type ShardedDatabaseHostBlockProvisionInput = {
  signal?: AbortSignal;
  databaseId?: string;
  title?: string;
  parentId?: string;
  managedBy?: string;
  props?: Record<string, unknown>;
  addBlock?: (
    flavour: 'affine:database',
    props: Record<string, unknown>,
    parentId: string
  ) => string | null | undefined;
};

/**
 * Runs inside the creation stage of a database storage root. `created` is true
 * only when this call materialized the root; an existing root is re-entered
 * for idempotent schema repair, so first-creation defaults must not be
 * rewritten then.
 */
export type DatabaseStorageInitializer = (
  model: DatabaseBlockModel,
  context: { created: boolean }
) => void;

export type ShardedDatabaseHostBlockProvisionResult =
  | {
      status: 'created';
      databaseBlockId: string;
      referenceProps: ShardedDatabaseReferenceProps;
      model: DatabaseBlockModel;
      storageModel: DatabaseBlockModel;
      /** The host block was added by this call (not an existing block reused). */
      created: boolean;
      /** Null when the storage already held this database unchanged. */
      receipt: LocalPublicationReceipt | null;
    }
  | { status: 'discarded' };

export type ShardedDatabaseBlockDataSourceProvisionInput =
  ShardedDatabaseHostBlockProvisionInput & {
    /** User-created blank tables start with one row in the creation publication. */
    initialEmptyRow?: boolean;
    initDataSource?: (dataSource: DatabaseBlockDataSource) => void;
  };

export type ShardedDatabaseBlockDataSourceProvisionResult =
  | (Extract<ShardedDatabaseHostBlockProvisionResult, { status: 'created' }> & {
      dataSource: DatabaseBlockDataSource;
    })
  | { status: 'discarded' };

export function selectChangedModelProps(
  model: DatabaseBlockModel,
  props: Record<string, unknown>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(props).filter(([key, value]) => {
      const current = model.props[key as keyof DatabaseBlockProps];
      if (current && value && key === 'title') {
        return current.toString() !== value.toString();
      }
      if (Object.is(current, value)) return false;
      try {
        return JSON.stringify(current) !== JSON.stringify(value);
      } catch {
        return true;
      }
    })
  );
}

export function updateModelPropsIfChanged(
  model: DatabaseBlockModel,
  props: Record<string, unknown>
): void {
  const changedProps = selectChangedModelProps(model, props);
  if (Object.keys(changedProps).length === 0) return;
  model.store.withoutTransact(() => {
    model.store.updateBlock(model, changedProps);
    Object.assign(model.props, changedProps);
  });
}

/**
 * The directory head stored on a database block. A File Index rebuilds its
 * rows from the attachments, so an unreadable head of it counts as none and
 * provisioning replaces it; every other database refuses it.
 */
function readProvisionedHead(
  block: YMap<unknown>,
  managedBy: string | undefined
): DatabaseShardDirectoryStoredHead | undefined {
  if (managedBy !== FILE_INDEX_MANAGED_BY)
    return readDatabaseShardDirectoryBlockHead(block);
  try {
    return readDatabaseShardDirectoryBlockHead(block);
  } catch (error) {
    if (
      error instanceof DatabaseShardDirectoryFormatError ||
      error instanceof UnsupportedDatabaseStorageVersionError
    )
      return undefined;
    throw error;
  }
}

/**
 * Directory part documents a replaced directory of the database left in
 * storage ({@link readStoredDirectoryParts}), and the part maps the
 * replacement writes them through. Only a File Index replaces a directory
 * with stored parts: its row shards take random ids, so a new shard never
 * lands on a stored one.
 */
export type ReplacedDatabaseDirectoryParts = {
  partMaps: DatabaseShardDirectoryPartMaps;
  storedPartIds: readonly string[];
};

export function ensureShardedDatabaseRootModelProps(
  model: DatabaseBlockModel,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title' | 'managedBy'>,
  replaced: ReplacedDatabaseDirectoryParts
): void {
  const databaseId = referenceProps.databaseId;
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseId || !databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database root model repair requires databaseId and databaseRootDocId.'
    );
  }

  // A host block not provisioned yet has no head; provisioning writes it.
  const head = readProvisionedHead(model.yBlock, input.managedBy);
  const headMatchesReference =
    head?.databaseId === databaseId &&
    head.databaseRootDocId === databaseRootDocId &&
    head.workspaceId === model.store.workspace.id;

  const props = {
    databaseId,
    databaseRootDocId,
    databaseStorageVersion: referenceProps.databaseStorageVersion,
    ...(model.props.title?.toString()
      ? {}
      : { title: textFromString(input.title ?? defaultDatabaseTitle()) }),
  };
  updateModelPropsIfChanged(model, props);
  if (headMatchesReference) return;
  // A block without a directory of this database starts an empty one. The
  // part documents a replaced directory left stay its parts, emptied, so a
  // part a later write adds never lands on a stored document.
  const { shards: _shards, ...fields } = createDatabaseShardManifest({
    databaseId,
    databaseRootDocId,
    workspaceId: model.store.workspace.id,
    shards: [],
  });
  model.store.transact(() => {
    deleteDatabaseShardDirectory(model.yBlock);
    for (const partId of replaced.storedPartIds)
      resetDatabaseShardDirectoryPart(
        replaced.partMaps(databaseShardDirectoryPartDocId(fields, partId)),
        { databaseId, partId }
      );
    writeDatabaseShardDirectoryHead(model.yBlock, {
      ...fields,
      parts: replaced.storedPartIds.map(partId =>
        summarizeDatabaseShardDirectoryPart(partId, [])
      ),
    });
  });
}

export function ensureShardedDatabaseHostModelProps(
  model: DatabaseBlockModel,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): void {
  const databaseId = referenceProps.databaseId;
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseId || !databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database host model repair requires databaseId and databaseRootDocId.'
    );
  }
  if (model.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${model.id}" is not a database block.`
    );
  }

  const props = {
    databaseId,
    databaseRootDocId,
    databaseStorageVersion: referenceProps.databaseStorageVersion,
    ...(model.props.title?.toString()
      ? {}
      : { title: textFromString(input.title ?? defaultDatabaseTitle()) }),
    // Canvas host blocks participate in edgeless layer ordering; a missing
    // fractional index poisons the layer comparator for the whole doc.
    ...(isCanvasHostParent(model.parent) && !model.props.index
      ? { index: 'a0' }
      : {}),
  };
  updateModelPropsIfChanged(model, props);
}

export function isCanvasHostParent(parent: BlockModel | null | undefined) {
  return parent?.role === 'root' || parent?.flavour === 'affine:surface';
}

export function assertCanvasHostHasXYWH(model: DatabaseBlockModel) {
  if (!isCanvasHostParent(model.parent)) {
    return;
  }

  if (isSerializedXYWH(model.props.xywh)) {
    return;
  }

  throw new BlockSuiteError(
    ErrorCode.DatabaseBlockError,
    `Canvas database host block "${model.id}" is missing xywh.`
  );
}

export function createShardedDatabaseReferenceForHostDoc(
  hostDoc: Store,
  input: ShardedDatabaseRootCreationInput = {}
): ShardedDatabaseReferenceProps {
  const databaseId = input.databaseId ?? hostDoc.workspace.idGenerator();
  const referenceProps = createDatabaseReferenceProps({
    databaseId,
    workspaceId: hostDoc.workspace.id,
  });
  const databaseRootDocId = referenceProps.databaseRootDocId;
  if (!databaseRootDocId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Database "${databaseId}" has no root document id.`
    );
  }
  return { id: databaseId, ...referenceProps };
}

/** Isolated real Stores preserve the schema/default-prop behavior of live Stores. */
function creationStores(
  host: Store,
  stage: DatabasePublicationStage,
  receipt: Promise<LocalPublicationReceipt | null>
) {
  const records = new Map<string, StoreDoc>();
  const stores = new Map<string, Store>();
  const disposers: Array<() => void> = [];
  const getRecord = (id: string): StoreDoc => {
    const previous = records.get(id);
    if (previous) return previous;
    const spaceDoc = stage.addEmptyDoc(id);
    const record = new Proxy(host.doc, {
      get(target, key) {
        if (key === 'id') return id;
        if (key === 'spaceDoc') return spaceDoc;
        if (key === 'yBlocks') return spaceDoc.getMap('blocks');
        if (key === 'workspace') return workspace;
        if (key === 'ready' || key === 'loaded') return true;
        if (key === 'load') return (init?: () => void) => init?.();
        if (key === 'getStore') return () => getStore(id);
        if (key === 'onLoadDoc' || key === 'connectAwareness') return () => {};
        return Reflect.get(target, key, target);
      },
    });
    records.set(id, record);
    return record;
  };
  const workspace = new Proxy(host.workspace, {
    get(target, key) {
      if (key === 'getDoc')
        return (id: string) =>
          stage.ids().includes(id) ? getRecord(id) : null;
      if (key === 'createDoc') return (id: string) => getRecord(id);
      if (key === 'docs') return records;
      if (key === 'registerDoc') return undefined;
      if (key === 'onLoadDoc' || key === 'onLoadAwareness') return () => {};
      return Reflect.get(target, key, target);
    },
  });
  const getStore = (id: string): Store => {
    const previous = stores.get(id);
    if (previous) return previous;
    const doc = getRecord(id);
    const store = new Store({
      doc,
      provider: host.provider,
      extensions: [...host.userExtensions, createDocBindingExtension(doc)],
    });
    stores.set(id, store);
    disposers.push(bindDatabaseCreationStage(store, { stage, receipt }));
    return store;
  };
  return {
    getStore,
    dispose: () => {
      disposers.forEach(dispose => dispose());
      for (const store of stores.values()) store.dispose();
    },
  };
}

/**
 * The page a creation send carries with a new database: the server binds the
 * database only to a page the same send shows holding its block.
 */
export type DatabaseCreationHosting = {
  /** Writes the page and returns its documents for the send; null when the
   * page is gone and nothing is published. */
  write(): readonly PublicationCompanion[] | null;
  /** Takes the write back after the send failed. */
  undo(): void;
};

/** The hosting of rows added to a database an earlier send created. */
const BOUND_DATABASE_HOSTING: DatabaseCreationHosting = {
  write: () => [],
  undo: () => {},
};

/**
 * The hosting of the databases of an archive import, by root document: the
 * archive pages that hold them, which the import writes as they are.
 */
export function archivePageHosting(
  pages: ReadonlyMap<string, readonly PublicationCompanion[]>
): (databaseRootDocId: string) => DatabaseCreationHosting {
  return rootDocId => {
    const companions = pages.get(rootDocId);
    if (!companions) throw new PublicationError('FORMAT_MISMATCH');
    return { write: () => companions, undo: () => {} };
  };
}

/** The hosting of a database block that `place` puts on the live page. */
export function livePageHosting(
  host: Store,
  place: () => string | null,
  remove: (blockId: string) => void
): DatabaseCreationHosting {
  let placed: string | null = null;
  return {
    write: () => {
      placed = place();
      return placed === null ? null : hostPageCompanions(host, placed);
    },
    undo: () => {
      if (placed !== null) remove(placed);
    },
  };
}

/** The page head and, if the block lives in a part, that part document. */
export function hostPageDocIds(host: Store, blockId: string): string[] {
  const partId = host.pageStructure.entry(blockId)?.s;
  if (partId === undefined) throw new PublicationError('FORMAT_MISMATCH');
  const pageId = host.doc.id;
  return partId === PAGE_DOCUMENT_PART_ID
    ? [pageId]
    : [
        pageId,
        createServerPagePartDocId({
          workspaceId: host.workspace.id,
          pageId,
          partId,
        }),
      ];
}

/**
 * The page documents that hold block `blockId` of `host`, in full: the server
 * reads the block over whatever of the page it stored before.
 */
function hostPageCompanions(
  host: Store,
  blockId: string
): PublicationCompanion[] {
  return hostPageDocIds(host, blockId).map(docId => {
    const doc = host.workspace.docs.get(docId)?.spaceDoc;
    if (!doc) throw new PublicationError('FORMAT_MISMATCH');
    return { docId, update: encodeStateAsUpdate(doc) };
  });
}

/** Commits a creation stage with its page; null when the page is gone. */
async function commitDatabaseCreationStage(
  host: Pick<Store, 'workspace'>,
  stage: DatabasePublicationStage,
  hosting: DatabaseCreationHosting,
  signal?: AbortSignal
): Promise<LocalPublicationReceipt | null> {
  if (signal?.aborted) throw new PublicationError('CANCELLED');
  const companions = hosting.write();
  if (companions === null) return null;
  let receipt: LocalPublicationReceipt;
  try {
    receipt = await stage.commit(
      getDatabaseWriterPorts(host.workspace).publication,
      host.workspace.id,
      'create',
      companions,
      signal
    );
  } catch (error) {
    hosting.undo();
    throw error;
  }
  for (const id of stage.ids()) {
    const record = ensureSystemWorkspaceDoc(host, id);
    applyUpdate(
      record.spaceDoc,
      stage.update(id),
      localPublicationApplyOrigin(receipt.operationId)
    );
  }
  return receipt;
}

/** Clipboard preparation uses the same actor allocation, commit and local-ACK boundary. */
export async function publishDatabaseCreationDocuments(
  host: Pick<Store, 'workspace'>,
  documents: readonly { docId: string; update: Uint8Array }[],
  hosting: DatabaseCreationHosting
): Promise<LocalPublicationReceipt | null> {
  const port = getDatabaseWriterPorts(host.workspace).publication;
  const bases = await port.readPublicationBase(documents.map(doc => doc.docId));
  if (bases.some(base => base.bin !== null || base.clock !== null))
    throw new PublicationError('LOCAL_CONFLICT');
  const stage = new DatabasePublicationStage(
    bases,
    [...host.workspace.docs.values()].map(doc => doc.spaceDoc.clientID)
  );
  try {
    for (const item of documents) {
      const source = new YDoc();
      try {
        applyUpdate(source, item.update);
        // An integrated Yjs type joins another document only as a clone.
        const copy = (value: unknown) =>
          value instanceof AbstractType
            ? value.clone()
            : structuredClone(value);
        for (const name of source.share.keys()) {
          if (databaseDocumentCollectionRoot(name) === 'array') {
            stage
              .doc(item.docId)
              .getArray(name)
              .push(source.getArray(name).toArray().map(copy));
            continue;
          }
          const from = source.getMap(name);
          const target = stage.doc(item.docId).getMap(name);
          from.forEach((value, key) => target.set(key, copy(value)));
        }
      } finally {
        source.destroy();
      }
    }
    const staged = new Set(stage.ids());
    for (const id of staged) {
      const blocks = stage.doc(id).getMap<YMap<unknown>>('blocks');
      const root = classifyPageSubdocId(id, host.workspace.id);
      const ids =
        root?.kind === 'database-root'
          ? [root.databaseId]
          : pageBlockIdsOfKind(
              pageReadSource(stage.doc(id), new Map()),
              'affine:database'
            );
      ids.forEach(blockId => {
        const block = blocks.get(blockId);
        if (!block) return;
        if (!blockHasDatabaseShardDirectory(block)) return;
        // The part documents of a directory publish with its root.
        const previous = readPublicationDirectory(
          block,
          publicationStageDocs(stage)
        );
        writeDatabaseRootDirectory(
          block,
          publicationDirectoryPartMaps(stage),
          createDatabaseShardManifest({
            ...previous,
            // Shards published by a later chunk keep their recorded size.
            shards: previous.shards.map(shard =>
              staged.has(shard.serverDocId)
                ? {
                    ...shard,
                    encodedBytes: encodeStateAsUpdate(
                      stage.doc(shard.serverDocId)
                    ).byteLength,
                  }
                : shard
            ),
          })
        );
      });
    }
    return await commitDatabaseCreationStage(host, stage, hosting);
  } finally {
    stage.destroy();
  }
}

/** Stage commit bounds: at most 64 members and 40 MiB per publication. */
const PUBLICATION_MEMBER_LIMIT = 64;
const PUBLICATION_BYTE_LIMIT = 40 * 1024 * 1024;

/**
 * Archive import publishes every imported database through the publication
 * journal: root first, then its row shards in bounded chunks. A direct write
 * into the system docs would never be sent to the server. The first send of a
 * database carries the page `hostingOf` its root names.
 */
export async function publishImportedDatabaseDocuments(
  host: Pick<Store, 'workspace'>,
  documents: readonly { docId: string; update: Uint8Array }[],
  hostingOf: (databaseRootDocId: string) => DatabaseCreationHosting
): Promise<LocalPublicationReceipt[]> {
  const workspaceId = host.workspace.id;
  // Root first, then its directory parts in the same publication, then the
  // row shards.
  const databases = new Map<
    string,
    { docId: string; update: Uint8Array; rank: number }[]
  >();
  for (const item of documents) {
    const subdoc = classifyPageSubdocId(item.docId, workspaceId);
    if (!subdoc || subdoc.kind === 'page_part' || subdoc.local)
      throw new PublicationError('FORMAT_MISMATCH');
    const databaseId = subdoc.databaseId;
    const members = databases.get(databaseId) ?? [];
    members.push({
      ...item,
      rank:
        subdoc.kind === 'database-root'
          ? 0
          : subdoc.kind === 'database-directory-part'
            ? 1
            : 2,
    });
    databases.set(databaseId, members);
  }
  const receipts: LocalPublicationReceipt[] = [];
  let hosting: DatabaseCreationHosting;
  const publish = async (chunk: { docId: string; update: Uint8Array }[]) => {
    const receipt = await publishDatabaseCreationDocuments(
      host,
      chunk,
      hosting
    );
    if (receipt) receipts.push(receipt);
    // Later sends add rows to the database the first one created.
    hosting = BOUND_DATABASE_HOSTING;
  };
  for (const members of databases.values()) {
    members.sort((a, b) => a.rank - b.rank);
    const [root] = members;
    if (root?.rank !== 0) throw new PublicationError('FORMAT_MISMATCH');
    hosting = hostingOf(root.docId);
    let chunk: { docId: string; update: Uint8Array }[] = [];
    let bytes = 0;
    for (const { docId, update, rank } of members) {
      if (
        rank === 2 &&
        (chunk.length === PUBLICATION_MEMBER_LIMIT ||
          (chunk.length && bytes + update.byteLength > PUBLICATION_BYTE_LIMIT))
      ) {
        await publish(chunk);
        chunk = [];
        bytes = 0;
      }
      chunk.push({ docId, update });
      bytes += update.byteLength;
    }
    if (chunk.length) await publish(chunk);
  }
  return receipts;
}

/** Documents a creation adds must not exist in local storage yet. */
async function assertEmptyPublicationBases(
  publication: ReturnType<typeof getDatabaseWriterPorts>['publication'],
  docIds: readonly string[],
  signal?: AbortSignal
): Promise<void> {
  if (!docIds.length) return;
  const bases = await publication.readPublicationBase(docIds, signal);
  if (
    bases.length !== docIds.length ||
    bases.some(
      (base, index) =>
        base.docId !== docIds[index] ||
        base.bin !== null ||
        base.clock !== null ||
        base.actor !== null
    )
  )
    throw new PublicationError('LOCAL_CONFLICT');
}

/**
 * The directory part documents of the database stored in local storage, with
 * their bases. Writers number parts from 00001 and never drop one from a
 * head, so the stored parts have no gap: the first missing id ends them.
 */
async function readStoredDirectoryParts(
  publication: ReturnType<typeof getDatabaseWriterPorts>['publication'],
  database: { workspaceId: string; databaseId: string },
  signal?: AbortSignal
): Promise<{ partId: string; base: PublicationMemberBase }[]> {
  const stored: { partId: string; base: PublicationMemberBase }[] = [];
  for (;;) {
    const partId = databaseShardDirectoryPartId(stored.length);
    const [base] = await publication.readPublicationBase(
      [databaseShardDirectoryPartDocId(database, partId)],
      signal
    );
    if (!base || (!base.bin && !base.clock && !base.actor)) return stored;
    stored.push({ partId, base });
  }
}

/**
 * Bases of the root document and of the directory parts the database keeps:
 * the parts its head names when it stores this database, else every stored
 * part a replaced directory left. A reused database publishes its parts as
 * members of the same stage, never as new empty documents.
 */
async function readStorageGroupBases(
  publication: ReturnType<typeof getDatabaseWriterPorts>['publication'],
  reference: { workspaceId: string; rootId: string; databaseId: string },
  managedBy: string | undefined,
  signal?: AbortSignal
): Promise<{ bases: PublicationMemberBase[]; storedPartIds: string[] }> {
  // The root names the parts, so they are read after it. A commit between
  // those reads would leave the stage on two different states; one read of
  // all members proves they belong to the same state.
  for (;;) {
    const planned = await planStorageGroupBases(
      publication,
      reference,
      managedBy,
      signal
    );
    const current = await publication.readPublicationBase(
      planned.bases.map(base => base.docId),
      signal
    );
    if (
      current.length === planned.bases.length &&
      planned.bases.every((base, index) => sameStoredBase(base, current[index]))
    )
      return planned;
  }
}

function sameStoredBase(
  planned: PublicationMemberBase,
  current: PublicationMemberBase | undefined
): boolean {
  return (
    current?.docId === planned.docId &&
    (current.clock?.getTime() ?? null) === (planned.clock?.getTime() ?? null) &&
    (current.actor?.clientId ?? null) === (planned.actor?.clientId ?? null) &&
    (current.actor?.nextClock ?? null) === (planned.actor?.nextClock ?? null)
  );
}

async function planStorageGroupBases(
  publication: ReturnType<typeof getDatabaseWriterPorts>['publication'],
  reference: { workspaceId: string; rootId: string; databaseId: string },
  managedBy: string | undefined,
  signal?: AbortSignal
): Promise<{ bases: PublicationMemberBase[]; storedPartIds: string[] }> {
  const { workspaceId, rootId, databaseId } = reference;
  const bases = [...(await publication.readPublicationBase([rootId], signal))];
  const root = bases.find(base => base.docId === rootId);
  if (!root?.bin) return { bases, storedPartIds: [] };
  const doc = new YDoc({ guid: rootId });
  let head: DatabaseShardDirectoryStoredHead | undefined;
  let stored = false;
  try {
    try {
      applyUpdate(doc, root.bin);
    } catch {
      throw new PublicationError('FORMAT_MISMATCH');
    }
    const block = doc.getMap('blocks').get(databaseId);
    stored = block instanceof YMap;
    head =
      block instanceof YMap ? readProvisionedHead(block, managedBy) : undefined;
  } finally {
    doc.destroy();
  }
  if (!stored) return { bases, storedPartIds: [] };
  if (
    head?.databaseId === databaseId &&
    head.databaseRootDocId === rootId &&
    head.workspaceId === workspaceId
  ) {
    const partDocIds = databaseShardDirectoryPartDocIds(head);
    return {
      bases: partDocIds.length
        ? [
            ...bases,
            ...(await publication.readPublicationBase(partDocIds, signal)),
          ]
        : bases,
      storedPartIds: [],
    };
  }
  // Provisioning replaces this head; it takes over the parts left stored.
  const parts = await readStoredDirectoryParts(
    publication,
    { workspaceId, databaseId },
    signal
  );
  // Only a File Index rebuilds its rows under random row shard ids. Any other
  // database whose head does not name its stored parts is broken: a new
  // directory would hand out the row shard ids its stored shards hold.
  if (parts.length && managedBy !== FILE_INDEX_MANAGED_BY)
    throw new DatabaseShardDirectoryFormatError(
      `Database "${databaseId}" stores directory parts its head does not name`
    );
  return {
    bases: [...bases, ...parts.map(part => part.base)],
    storedPartIds: parts.map(part => part.partId),
  };
}

async function createDatabaseStorageGroup(
  host: Store,
  reference: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title' | 'managedBy'>,
  hosting: DatabaseCreationHosting,
  initialize?: DatabaseStorageInitializer,
  signal?: AbortSignal
): Promise<{
  model: DatabaseBlockModel;
  receipt: LocalPublicationReceipt | null;
} | null> {
  const { publication } = getDatabaseWriterPorts(host.workspace);
  const rootId = reference.databaseRootDocId;
  const databaseId = reference.databaseId;
  if (!rootId || !databaseId) throw new PublicationError('FORMAT_MISMATCH');
  const { bases, storedPartIds } = await readStorageGroupBases(
    publication,
    { workspaceId: host.workspace.id, rootId, databaseId },
    input.managedBy,
    signal
  );
  const liveIds = [...host.workspace.docs.values()].map(
    doc => doc.spaceDoc.clientID
  );
  const stage = new DatabasePublicationStage(bases, liveIds);
  const completed = Promise.withResolvers<LocalPublicationReceipt | null>();
  completed.promise.catch(() => {});
  const isolated = creationStores(host, stage, completed.promise);
  try {
    const root = isolated.getStore(rootId);
    await root.pageStructure.loadMissingPartsOf([databaseId]);
    let model = root.getModelById<DatabaseBlockModel>(databaseId);
    if (model && model.flavour !== 'affine:database')
      throw new PublicationError('FORMAT_MISMATCH');
    const created = !model;
    if (!model) {
      const initialShard = createDatabaseShardManifestEntry({
        workspaceId: host.workspace.id,
        databaseId,
        shardId:
          input.managedBy === FILE_INDEX_MANAGED_BY
            ? nextFileIndexWriterShardId(host.doc.spaceDoc, [])
            : '00001',
        rowCount: 0,
        encodedBytes: 0,
        status: 'ready',
      });
      await assertEmptyPublicationBases(
        publication,
        [initialShard.serverDocId],
        signal
      );
      stage.addEmptyDoc(initialShard.serverDocId);
      const page =
        root.root?.id ?? root.addBlock('affine:page', { title: new Text('') });
      const shard = stage.doc(initialShard.serverDocId);
      shard.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY);
      shard.getArray(DATABASE_ROW_SHARD_ORDER_ARRAY_KEY);
      root.addBlock(
        'affine:database',
        {
          id: databaseId,
          ...createShardedDatabaseIdentityProps({
            databaseId,
            databaseRootDocId: rootId,
            workspaceId: host.workspace.id,
          }),
          ...(input.managedBy ? { managedBy: input.managedBy } : {}),
          cells: Object.create(null),
          columns: [],
          canvasPresentation: createCanvasSummaryPresentationState(),
          title: textFromString(input.title ?? defaultDatabaseTitle()),
          views: [],
        },
        page
      );
      if (input.managedBy === FILE_INDEX_MANAGED_BY) {
        const entry = {
          ...initialShard,
          encodedBytes: encodeStateAsUpdate(shard).byteLength,
        };
        writeFileIndexShardEntry(
          getFileIndexShardRegistry(stage.doc(rootId), databaseId),
          entry
        );
      }
      model = root.getModelById<DatabaseBlockModel>(databaseId);
      if (!model) throw new PublicationError('FORMAT_MISMATCH');
      const partDocIds = writeDatabaseRootDirectory(
        model.yBlock,
        publicationDirectoryPartMaps(stage),
        createDatabaseShardManifest({
          databaseId,
          databaseRootDocId: rootId,
          workspaceId: host.workspace.id,
          shards: [
            {
              ...initialShard,
              encodedBytes: encodeStateAsUpdate(shard).byteLength,
            },
          ],
        })
      );
      await assertEmptyPublicationBases(publication, partDocIds, signal);
    }
    if (!model) throw new PublicationError('FORMAT_MISMATCH');
    ensureShardedDatabaseRootModelProps(model, reference, input, {
      partMaps: publicationDirectoryPartMaps(stage),
      storedPartIds,
    });
    initialize?.(model, { created });
    // A storage that already holds this database publishes nothing; its page
    // block is written like any page edit.
    let receipt: LocalPublicationReceipt | null = null;
    if (stage.changed()) {
      receipt = await commitDatabaseCreationStage(host, stage, hosting, signal);
      // A page gone before the commit leaves nothing published.
      if (!receipt) {
        completed.resolve(null);
        return null;
      }
    }
    completed.resolve(receipt);
    const live = getSystemWorkspaceStore(
      host,
      rootId
    ).getModelById<DatabaseBlockModel>(databaseId);
    if (!live) throw new PublicationError('FORMAT_MISMATCH');
    return { model: live, receipt };
  } catch (error) {
    completed.reject(error);
    throw error;
  } finally {
    isolated.dispose();
    stage.destroy();
  }
}

export async function ensureShardedDatabaseRootForHostDoc(
  hostDoc: Store,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): Promise<ShardedDatabaseReferenceProps> {
  await ensureShardedDatabaseRootModelForHostDoc(
    hostDoc,
    referenceProps,
    input
  );
  return referenceProps;
}
/** Creates the storage of a database block the page already holds. */
export async function ensureShardedDatabaseRootModelForHostDoc(
  hostDoc: Store,
  referenceProps: ShardedDatabaseReferenceProps,
  input: Pick<ShardedDatabaseRootCreationInput, 'title'> = {}
): Promise<DatabaseBlockModel> {
  const placed = () => {
    if (
      hostDoc.getBlock(referenceProps.id)?.model.flavour !== 'affine:database'
    )
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        `Database "${referenceProps.id}" has no block on its page.`
      );
    return referenceProps.id;
  };
  const group = await createDatabaseStorageGroup(
    hostDoc,
    referenceProps,
    input,
    livePageHosting(hostDoc, placed, () => {})
  );
  // `placed` throws instead of reporting a gone page, so a group returns.
  return group!.model;
}

const pendingHostCreations = new WeakMap<
  Store,
  Map<string, Promise<ShardedDatabaseHostBlockProvisionResult>>
>();
export async function provisionShardedDatabaseHostBlock(
  host: Store,
  input: ShardedDatabaseHostBlockProvisionInput,
  initializeStorage?: DatabaseStorageInitializer
): Promise<ShardedDatabaseHostBlockProvisionResult> {
  const databaseId = input.databaseId ?? host.workspace.idGenerator();
  let pending = pendingHostCreations.get(host);
  if (!pending) pendingHostCreations.set(host, (pending = new Map()));
  const previous = pending.get(databaseId);
  if (previous) {
    const result = await previous;
    return result.status === 'created' ? { ...result, created: false } : result;
  }
  const created = provisionShardedDatabaseHostBlockNow(
    host,
    { ...input, databaseId },
    initializeStorage
  );
  pending.set(databaseId, created);
  try {
    return await created;
  } finally {
    if (pending.get(databaseId) === created) pending.delete(databaseId);
  }
}

async function provisionShardedDatabaseHostBlockNow(
  hostDoc: Store,
  input: ShardedDatabaseHostBlockProvisionInput,
  initializeStorage?: DatabaseStorageInitializer
): Promise<ShardedDatabaseHostBlockProvisionResult> {
  const title = input.title ?? defaultDatabaseTitle();
  const parentId = input.parentId ?? hostDoc.root?.id;
  if (!parentId) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      'Sharded database host creation requires a parent block id.'
    );
  }

  const referenceProps = createShardedDatabaseReferenceForHostDoc(hostDoc, {
    databaseId: input.databaseId,
    title,
  });
  const addBlock =
    input.addBlock ??
    ((flavour, props, parentId) =>
      hostDoc.addBlock(flavour, props as never, parentId));
  const existingHostBlock = hostDoc.getBlock(referenceProps.id)?.model;
  if (existingHostBlock && existingHostBlock.flavour !== 'affine:database') {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${referenceProps.id}" is not a database block.`
    );
  }
  if (
    !existingHostBlock &&
    hostDoc.pageStructure.blockFields(referenceProps.id)
  ) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${referenceProps.id}" exists but is not materialized.`
    );
  }
  if (
    !existingHostBlock &&
    isCanvasHostParent(hostDoc.getBlock(parentId)?.model) &&
    !isSerializedXYWH(input.props?.xywh)
  ) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Canvas database host block "${referenceProps.id}" is missing xywh.`
    );
  }

  let placed: string | null = null;
  const place = (): string | null => {
    if (existingHostBlock) return (placed = existingHostBlock.id);
    if (
      !hostDoc.workspace.getDoc(hostDoc.id) ||
      !hostDoc.getModelById(parentId)
    )
      return null;
    const id = addBlock(
      'affine:database',
      {
        ...referenceProps,
        title: textFromString(title),
        ...(input.props ?? {}),
      },
      parentId
    );
    if (!id) {
      throw new BlockSuiteError(
        ErrorCode.DatabaseBlockError,
        'Sharded database host creation returned an empty block id.'
      );
    }
    return (placed = id);
  };
  const group = await createDatabaseStorageGroup(
    hostDoc,
    referenceProps,
    { title, managedBy: input.managedBy },
    livePageHosting(hostDoc, place, blockId => {
      if (blockId === existingHostBlock?.id) return;
      const block = hostDoc.getBlock(blockId)?.model;
      if (block) hostDoc.deleteBlock(block);
    }),
    initializeStorage,
    input.signal
  );
  if (!group) return { status: 'discarded' };
  const { model: storageModel, receipt } = group;
  // A storage that published nothing leaves its block to a page edit.
  const databaseBlockId = placed ?? place();
  if (databaseBlockId === null) return { status: 'discarded' };

  const model = hostDoc.getBlock(databaseBlockId)?.model as
    | DatabaseBlockModel
    | undefined;
  if (!model) {
    throw new BlockSuiteError(
      ErrorCode.DatabaseBlockError,
      `Sharded database host "${databaseBlockId}" was not materialized.`
    );
  }

  ensureShardedDatabaseHostModelProps(model, referenceProps, {
    title,
  });
  assertCanvasHostHasXYWH(model);
  return {
    status: 'created',
    databaseBlockId,
    referenceProps,
    model,
    storageModel,
    receipt,
    created: !existingHostBlock,
  };
}
